import { createContext, useContext } from "react";

import { createStore, useStore } from "zustand";

type SelectedCell = {
  row: number;
  col: string;
  editing: boolean;
};

type EditableTableSelectionState = {
  selectedCell: SelectedCell | null;
};

type EditableTableSelectionActions = {
  selectCell: (row: number, col: string, editing?: boolean) => void;
  deselectCell: () => void;
  /** Exit editing mode. When `forRow`/`forCol` are given the action is a
   *  no-op if the selection has already moved to a different cell. This
   *  prevents a stale click-outside handler from closing a newly-opened
   *  editor on a different cell. */
  exitEditing: (forRow?: number, forCol?: string) => void;
  setEditing: () => void;
  adjustForRowInsert: (afterIndex: number) => void;
  adjustForRowDelete: (deletedIndex: number) => void;
};

type EditableTableSelectionStoreState = EditableTableSelectionState & {
  actions: EditableTableSelectionActions;
};

export type EditableTableSelectionStore = ReturnType<
  typeof createEditableTableSelectionStore
>;

export const createEditableTableSelectionStore = () =>
  createStore<EditableTableSelectionStoreState>((set) => ({
    selectedCell: null,

    actions: {
      selectCell: (row, col, editing = false) =>
        set({ selectedCell: { row, col, editing } }),

      deselectCell: () => set({ selectedCell: null }),

      exitEditing: (forRow?: number, forCol?: string) =>
        set((state) => {
          if (!state.selectedCell) return state;
          // When called with a specific cell, skip if selection moved away.
          if (
            forRow !== undefined &&
            forCol !== undefined &&
            (state.selectedCell.row !== forRow ||
              state.selectedCell.col !== forCol)
          ) {
            return state;
          }
          return {
            selectedCell: { ...state.selectedCell, editing: false },
          };
        }),

      setEditing: () =>
        set((state) => ({
          selectedCell: state.selectedCell
            ? { ...state.selectedCell, editing: true }
            : null,
        })),

      adjustForRowInsert: (afterIndex) =>
        set((state) => {
          if (!state.selectedCell || state.selectedCell.row <= afterIndex)
            return state;
          return {
            selectedCell: {
              ...state.selectedCell,
              row: state.selectedCell.row + 1,
            },
          };
        }),

      adjustForRowDelete: (deletedIndex) =>
        set((state) => {
          if (!state.selectedCell) return state;
          if (state.selectedCell.row === deletedIndex)
            return { selectedCell: null };
          if (state.selectedCell.row > deletedIndex) {
            return {
              selectedCell: {
                ...state.selectedCell,
                row: state.selectedCell.row - 1,
              },
            };
          }
          return state;
        }),
    },
  }));

const EditableTableSelectionContext =
  createContext<EditableTableSelectionStore | null>(null);

export const EditableTableSelectionProvider =
  EditableTableSelectionContext.Provider;

const useEditableTableStore = () => {
  const store = useContext(EditableTableSelectionContext);
  if (!store) throw new Error("Missing EditableTableSelectionProvider");
  return store;
};

export const useSelectionActions = () => {
  const store = useEditableTableStore();
  return useStore(store, (s) => s.actions);
};

export const useIsCellSelected = (row: number, col: string) => {
  const store = useEditableTableStore();
  return useStore(
    store,
    (s) => s.selectedCell?.row === row && s.selectedCell?.col === col,
  );
};

export const useIsCellEditing = (row: number, col: string) => {
  const store = useEditableTableStore();
  return useStore(
    store,
    (s) =>
      s.selectedCell?.row === row &&
      s.selectedCell?.col === col &&
      (s.selectedCell?.editing ?? false),
  );
};
